import Component from "@glimmer/component";
import { fn, hash } from "@ember/helper";
import { trustHTML } from "@ember/template";
import { modifier as modifierFn } from "ember-modifier";
import DFloatPortal from "discourse/float-kit/components/d-float-portal";
import { getScrollParent } from "discourse/float-kit/lib/get-scroll-parent";
import FloatKitApplyFloatingUi from "discourse/float-kit/modifiers/apply-floating-ui";
import FloatKitCloseOnEscape from "discourse/float-kit/modifiers/close-on-escape";
import dConcatClass from "discourse/ui-kit/helpers/d-concat-class";
import dCloseOnClickOutside from "discourse/ui-kit/modifiers/d-close-on-click-outside";
import dTrapTab from "discourse/ui-kit/modifiers/d-trap-tab";

export default class DFloatBody extends Component {
  closeOnScroll = modifierFn(() => {
    const firstScrollParent = getScrollParent(this.trigger);

    const handler = () => {
      this.args.instance.close();
    };

    firstScrollParent.addEventListener("scroll", handler, { passive: true });

    return () => {
      firstScrollParent.removeEventListener("scroll", handler);
    };
  });

  trapInteractionPropagation = modifierFn((element) => {
    const handler = (event) => {
      event.stopPropagation();
    };

    const events = ["pointerdown", "mousedown", "touchend"];
    events.forEach((name) => element.addEventListener(name, handler));

    return () => {
      events.forEach((name) => element.removeEventListener(name, handler));
    };
  });

  get supportsCloseOnClickOutside() {
    return this.options.closeOnClickOutside;
  }

  get supportsCloseOnEscape() {
    return this.options.closeOnEscape;
  }

  get supportsCloseOnScroll() {
    return this.options.closeOnScroll;
  }

  get trigger() {
    return this.args.instance?.trigger;
  }

  get content() {
    return this.args.instance?.content;
  }

  get options() {
    return this.args.instance.options;
  }

  get style() {
    const maxWidth =
      typeof this.options.maxWidth === "number"
        ? `${this.options.maxWidth}px`
        : this.options.maxWidth;

    return trustHTML(`max-width: ${maxWidth}`);
  }

  <template>
    {{~! strip whitespace ~}}<DFloatPortal
      @inline={{@inline}}
      @portalOutletElement={{@instance.portalOutletElement}}
    >
      {{! eslint-disable-next-line ember/template-no-unsupported-role-attributes }}
      <div
        class={{dConcatClass
          @mainClass
          (if this.options.animated "-animated")
          (if @instance.expanded "-expanded")
        }}
        data-identifier={{this.options.identifier}}
        data-content
        aria-labelledby={{@instance.id}}
        aria-expanded={{if @instance.expanded "true" "false"}}
        role={{@role}}
        {{FloatKitApplyFloatingUi this.trigger this.options @instance}}
        {{this.trapInteractionPropagation}}
        {{(if @trapTab (modifier dTrapTab autofocus=this.options.autofocus))}}
        {{(if
          this.supportsCloseOnClickOutside
          (modifier
            dCloseOnClickOutside
            (fn @instance.close (hash focusTrigger=false))
            (hash target=this.content)
          )
        )}}
        {{(if
          this.supportsCloseOnEscape
          (modifier FloatKitCloseOnEscape @instance.close)
        )}}
        {{(if this.supportsCloseOnScroll (modifier this.closeOnScroll))}}
        style={{this.style}}
        ...attributes
      >
        <div class={{@innerClass}}>
          {{yield}}
        </div>
      </div>
    </DFloatPortal>{{~! strip whitespace ~}}
  </template>
}
